import { useState, useEffect, useCallback, useRef } from 'react';
import { getLeaderboardTracks, getLeaderboard, mergeTracks, unmergeTrack, reassignSessionTrack, renameLeaderboardTrack, getFollowing, followUser, unfollowUser, getTracks } from '../../services/api';
import { getCurrentUserId } from '../../services/auth';
import { LAP_COLORS, COMPARE_PALETTES } from '../LapSelector/LapSelector';
import ReassignTrackModal from '../common/ReassignTrackModal';
import styles from './LeaderboardPage.module.css';

// Max laps selectable for a side-by-side comparison: 1 primary + 3 comparison.
const MAX_COMPARE_SEL = 4;
// Marker color for each comparison slot, matching the analysis-page overlays:
// position 0 = primary, positions 1..3 = comparison palettes.
const compareColor = (i) => (i === 0 ? LAP_COLORS[0] : COMPARE_PALETTES[i - 1][0]);
const CIRCLED = ['①', '②', '③', '④'];

// ── Unit helpers ──────────────────────────────────────────────────────────────
function msToSpeed(ms, prefs) {
  if (ms == null || isNaN(ms)) return null;
  return prefs?.speed === 'km/h' ? ms * 3.6 : ms * 2.23694;
}
function speedLabel(prefs) {
  return prefs?.speed === 'km/h' ? 'km/h' : 'mph';
}
function mToDistance(m, prefs) {
  if (m == null || isNaN(m)) return null;
  return prefs?.speed === 'km/h' ? m * 3.28084 : m * 3.28084;
}
function distanceLabel(prefs) {
  return 'ft';
}
function fmt(val, decimals = 2) {
  if (val == null || isNaN(val)) return '—';
  return val.toFixed(decimals);
}
function fmtPct(val, decimals = 0) {
  if (val == null || isNaN(val)) return '—';
  return `${(val * 100).toFixed(decimals)}%`;
}

// ── Best-lap collapse ─────────────────────────────────────────────────────────
// The raw API returns every visible lap. The leaderboard shows one row per
// competitor — their fastest lap per car — so a driver who logged 30 good laps
// can't flood the board. Each competitor's remaining laps are stashed on
// `otherLaps` (slowest→fastest minus the best) for the drill-down. `rank` is the
// leaderboard position by lap time, computed over the collapsed set so it stays
// stable no matter which column the table is currently sorted by.
//
// Collapse key is (user_id, vehicle_id): the same driver in two cars is two rows,
// which matches the vehicle filter and how vehicles are tracked everywhere else.
// NOTE: collapse + drill-down happen client-side because every lap is already
// shipped to the browser. If a single track ever holds thousands of laps, move
// this into SQL (DISTINCT ON (user_id, vehicle_id)) + a lazy per-driver endpoint.
function collapseToBestLaps(entries) {
  const lapTime = e => e.lap_time_seconds ?? Infinity;
  const groups = new Map();
  for (const e of entries) {
    const key = `${e.user_id}|${e.vehicle_id}`;
    const g = groups.get(key);
    if (g) g.push(e); else groups.set(key, [e]);
  }
  const primaries = [];
  for (const laps of groups.values()) {
    const sorted = [...laps].sort((a, b) => lapTime(a) - lapTime(b));
    const [best, ...rest] = sorted;
    primaries.push({ ...best, otherLaps: rest });
  }
  primaries.sort((a, b) => lapTime(a) - lapTime(b));
  primaries.forEach((p, i) => { p.rank = i + 1; });
  return primaries;
}

// Sort a list of entries by a column key/direction, using the column's getValue.
// Shared by the main table and each driver's per-block lap sort. Returns a new array.
function sortByCol(entries, sortCol, sortDir, cols) {
  const col = cols.find(c => c.key === sortCol);
  if (!col) return [...entries];
  return [...entries].sort((a, b) => {
    const va = col.getValue(a);
    const vb = col.getValue(b);
    const cmp = typeof va === 'string'
      ? va.localeCompare(vb)
      : (va < vb ? -1 : va > vb ? 1 : 0);
    return sortDir === 'asc' ? cmp : -cmp;
  });
}

// Default sort for a driver's expanded lap block: fastest first (matches the
// order otherLaps already arrive in from collapseToBestLaps).
const DEFAULT_LAP_SORT = { col: 'lap_time_seconds', dir: 'asc' };

// A compact, full-width control row placed at the top of an expanded driver's
// block. It sorts only that one driver's other laps, independent of the main
// table and of any other expanded block.
function DriverLapsSortRow({ entryId, cols, sort, onChange, colSpan, styles }) {
  return (
    <tr className={styles.driverSortRow}>
      <td colSpan={colSpan} className={styles.driverSortCell} onClick={e => e.stopPropagation()}>
        <span className={styles.muted}>↳ Sort these laps:</span>
        <select
          className={styles.driverSortSelect}
          value={sort.col}
          onChange={e => {
            const c = cols.find(c => c.key === e.target.value);
            onChange(entryId, { col: e.target.value, dir: c?.defaultDir || 'asc' });
          }}
        >
          {cols.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        <button
          className={styles.driverSortDir}
          onClick={() => onChange(entryId, { ...sort, dir: sort.dir === 'asc' ? 'desc' : 'asc' })}
          title={sort.dir === 'asc' ? 'Ascending — click for descending' : 'Descending — click for ascending'}
        >{sort.dir === 'asc' ? '▲' : '▼'}</button>
      </td>
    </tr>
  );
}

// ── Column definitions ────────────────────────────────────────────────────────
// Each col: { key, label, subLabel, tooltip, getValue, render, defaultDir }
function makeCols(prefs) {
  const sp = speedLabel(prefs);
  return [
    { key: 'rank',
      label: '#', subLabel: '', defaultDir: 'asc',
      tooltip: 'Current rank by lap time (fastest = #1). Re-ranks when you sort by another column.',
      getValue: e => e.rank ?? Infinity,
      render:   e => <span className={styles.rankNum}>{e.rank}</span> },

    { key: 'user_display_name',
      label: 'Driver', subLabel: '', defaultDir: 'asc',
      tooltip: 'Driver name. Hover a cell to see the email address.',
      getValue: e => (e.user_display_name || e.user_email || '').toLowerCase(),
      render:   e => <span title={e.user_email}>{e.user_display_name || e.user_email || '—'}</span> },

    { key: 'vehicle_name',
      label: 'Car', subLabel: '', defaultDir: 'asc',
      tooltip: 'Vehicle / car name. Use the filter chips above to isolate a single car.',
      getValue: e => (e.vehicle_name || '').toLowerCase(),
      render:   e => e.vehicle_name || '—' },

    { key: 'event_date',
      label: 'Date', subLabel: '', defaultDir: 'desc',
      tooltip: 'Date of the track session this lap came from.',
      getValue: e => e.event_date || '',
      render:   e => <span className={styles.muted}>{e.event_date || '—'}</span> },

    { key: 'lap_time_seconds',
      label: 'Lap Time', subLabel: '', defaultDir: 'asc',
      tooltip: 'Total lap time (mm:ss.mmm). The default ranking metric — lower is better.',
      getValue: e => e.lap_time_seconds ?? Infinity,
      render:   e => <span className={styles.lapTime}>{e.lap_time_str}</span> },

    { key: 'driver_score',
      label: 'TreyScore', subLabel: '(0–100)', defaultDir: 'desc',
      tooltip: 'How much of the session\'s demonstrated grip the driver used while grip was available to use: average combined G over the lap\'s "active" samples (cornering, braking, coasting — everything except power-limited acceleration on a straight) ÷ the session\'s demonstrated grip (the best lap-sustained combined G of any timed lap in the session — a 95th-percentile statistic chosen so GPS-derived and IMU accelerometer sources score comparably). Coasting counts as ~zero grip used, so wasted time lowers the score in proportion. 100 = at the session\'s grip limit whenever the tires had something to give.',
      getValue: e => e.driver_score ?? -Infinity,
      render:   e => e.driver_score == null ? '—' : fmt(e.driver_score * 100, 1) },

    { key: 'top_speed_ms',
      label: 'Top Speed', subLabel: `(${sp})`, defaultDir: 'desc',
      tooltip: `Highest GPS speed recorded at any point during the lap (${sp}). Reflects the car's peak straight-line velocity.`,
      getValue: e => e.top_speed_ms ?? -Infinity,
      render:   e => fmt(msToSpeed(e.top_speed_ms, prefs), 1) },

    { key: 'avg_lap_speed_ms',
      label: 'Avg Speed', subLabel: `(${sp})`, defaultDir: 'desc',
      tooltip: `Average GPS speed across the entire lap (${sp}). Computed as the arithmetic mean of all GPS speed samples (time-weighted at 25 Hz) — not distance ÷ time. Time spent in slow corners contributes proportionally more, so it's a true reflection of pace across the full lap duration. Higher = less time lost in corners.`,
      getValue: e => e.avg_lap_speed_ms ?? -Infinity,
      render:   e => fmt(msToSpeed(e.avg_lap_speed_ms, prefs), 1) },

    { key: 'total_distance_m',
      label: 'Distance', subLabel: `(${distanceLabel(prefs)})`, defaultDir: 'desc',
      tooltip: `Total GPS distance traveled during the lap (ft). Computed as the sum of consecutive haversine steps between GPS samples. Reflects the actual path driven, including any line variation.`,
      getValue: e => e.total_distance_m ?? -Infinity,
      render:   e => fmt(mToDistance(e.total_distance_m, prefs), 3) },

    { key: 'chris_avg_speed',
      label: 'ChrisAvgSpeed', subLabel: `(${sp})`, defaultDir: 'desc',
      tooltip: `Chris's average speed: total GPS distance traveled ÷ lap time (${sp}). Unlike the sample-mean Avg Speed, this is pure distance ÷ time — the same number a stopwatch and odometer would give you. The two values are close but not identical: this version is slightly lower when the GPS track is shorter than the "true" ideal line.`,
      getValue: e => (e.total_distance_m != null && e.lap_time_seconds)
        ? e.total_distance_m / e.lap_time_seconds
        : -Infinity,
      render: e => {
        if (e.total_distance_m == null || !e.lap_time_seconds) return '—';
        return fmt(msToSpeed(e.total_distance_m / e.lap_time_seconds, prefs), 1);
      } },

    { key: 'peak_lat_g',
      label: 'Peak Lat G', subLabel: '(g)', defaultDir: 'desc',
      tooltip: 'Peak lateral (cornering) G — the 99.5th percentile of |lateral G|, not the raw single-sample max, so brief GPS/sensor spikes don\'t inflate it. Read from the inertial accelerometer (LatAcc/AccLat) when present, falling back to GPS-derived LatAcc. Reflects the sustained grip used at the limit mid-corner; higher = closer to the tire\'s lateral grip limit.',
      getValue: e => e.peak_lat_g ?? -Infinity,
      render:   e => fmt(e.peak_lat_g) },

    { key: 'peak_braking_g',
      label: 'Peak Brake G', subLabel: '(g)', defaultDir: 'desc',
      tooltip: 'Peak braking deceleration in G (stored positive, from GPS LonAcc) — the single hardest braking sample (raw peak). Longitudinal G is a clean, physically bounded signal, so no spike-rejection is applied here. Reflects how hard the driver braked at the deepest braking point.',
      getValue: e => e.peak_braking_g ?? -Infinity,
      render:   e => fmt(e.peak_braking_g) },

    { key: 'peak_drive_g',
      label: 'Peak Drive G', subLabel: '(g)', defaultDir: 'desc',
      tooltip: 'Peak forward acceleration G from GPS LonAcc — the single best acceleration sample (raw peak). Longitudinal G is clean and engine-limited, so no spike-rejection is applied. Reflects how aggressively the driver applied throttle at the best exit point of the lap.',
      getValue: e => e.peak_drive_g ?? -Infinity,
      render:   e => fmt(e.peak_drive_g) },

    { key: 'pct_time_braking',
      label: '% Braking', subLabel: '', defaultDir: 'asc',
      tooltip: 'Percentage of lap time spent with longitudinal deceleration above 0.1 g. A lower value generally means less time on the brakes and more efficient corner entries.',
      getValue: e => e.pct_time_braking ?? Infinity,
      render:   e => fmtPct(e.pct_time_braking) },

    { key: 'pct_time_accelerating',
      label: '% Accel', subLabel: '', defaultDir: 'desc',
      tooltip: 'Percentage of lap time spent accelerating (lon G > 0.1 g). Higher values mean the driver spent more of the lap on the throttle — generally a sign of clean, early exits.',
      getValue: e => e.pct_time_accelerating ?? -Infinity,
      render:   e => fmtPct(e.pct_time_accelerating) },

    { key: 'pct_time_coasting',
      label: '% Coasting', subLabel: '', defaultDir: 'asc',
      tooltip: 'Percentage of lap time spent coasting — off the throttle but not braking, not cornering (lateral G under 0.25 g), and moving: straight-line time with neither pedal doing real work. When a usable throttle channel is available, "off the throttle" means throttle near its closed position (within 10% of the channel\'s own range, so a sensor that reads a non-zero value when closed is handled correctly) — this excludes an underpowered car at full throttle that simply is not accelerating hard (e.g. uphill or near top speed), which a G-only test misreads as coasting. For GPS-only sessions, or when the throttle channel is unreliable (e.g. it stays high during hard braking), it falls back to longitudinal G in the dead zone between braking and accelerating (within ±0.1 g). Lower generally means crisper brake-to-throttle transitions. Hover a value for the time in seconds.',
      getValue: e => e.pct_time_coasting ?? Infinity,
      render: e => {
        if (e.pct_time_coasting == null) return '—';
        const secs = e.lap_time_seconds != null
          ? `${(e.pct_time_coasting * e.lap_time_seconds).toFixed(2)} s coasting`
          : undefined;
        return <span title={secs}>{fmtPct(e.pct_time_coasting, 2)}</span>;
      } },

    { key: 'peak_combined_g',
      label: 'Peak Comb G', subLabel: '(g)', defaultDir: 'desc',
      tooltip: 'Peak combined G — √(LatAcc² + LonAcc²), the outermost point on the traction circle, reported as the 99.5th percentile (robust to single-sample spikes). Lateral and longitudinal samples are time-aligned before combining, and the lateral axis uses the same inertial-preferred channel as Peak Lat G. Shows how close to the absolute grip limit the car got at its hardest moment.',
      getValue: e => e.peak_combined_g ?? -Infinity,
      render:   e => fmt(e.peak_combined_g) },

    { key: 'avg_traction_utilization',
      label: 'Avg Traction', subLabel: '', defaultDir: 'desc',
      tooltip: 'Average traction circle utilization across the lap (avg combined G ÷ peak combined G). Higher = more consistently near the grip limit throughout the lap, not just in one spot.',
      getValue: e => e.avg_traction_utilization ?? -Infinity,
      render:   e => fmtPct(e.avg_traction_utilization) },

    { key: 'peak_yaw_rate',
      label: 'Peak Yaw', subLabel: '(°/s)', defaultDir: 'desc',
      tooltip: 'Peak yaw rate in °/s — how quickly the car rotated, taken as the 99.5th percentile (robust to spikes) rather than the raw max. Prefers the inertial gyro (YawRate) over the noisier GPS-derived yaw when both are present. High values can indicate aggressive rotation, oversteer catches, or very tight corners.',
      getValue: e => e.peak_yaw_rate ?? -Infinity,
      render:   e => fmt(e.peak_yaw_rate, 1) },

    { key: 'oversteer_index',
      label: 'Oversteer', subLabel: '(s)', defaultDir: 'desc',
      tooltip: 'Total seconds per lap where actual yaw rate exceeded the predicted neutral-steer yaw rate (calculated from lateral G and speed). Uses the inertial-preferred yaw and lateral channels. Higher = more oversteer or snap rotation events.',
      getValue: e => e.oversteer_index ?? -Infinity,
      render:   e => fmt(e.oversteer_index, 2) },

    { key: 'steering_smoothness',
      label: 'Smoothness', subLabel: '↓ better', defaultDir: 'asc',
      tooltip: 'Standard deviation of lateral acceleration (inertial-preferred channel) — a proxy for steering input smoothness. Lower is better: smooth, consistent inputs produce a lower number than jerky or reactive corrections.',
      getValue: e => e.steering_smoothness ?? Infinity,
      render:   e => fmt(e.steering_smoothness, 3) },

    { key: 'peak_pitch_rate',
      label: 'Peak Pitch', subLabel: '(°/s)', defaultDir: 'desc',
      tooltip: 'Peak pitch rate in °/s — the 99.5th percentile (robust to spikes; differentiated rate channels are inherently noisy), not the raw max. Indicates the intensity of weight transfer under braking (nose dive) and acceleration (squat). Very high values may suggest abrupt inputs or a rough surface.',
      getValue: e => e.peak_pitch_rate ?? -Infinity,
      render:   e => fmt(e.peak_pitch_rate, 1) },
  ];
}

// ── Vehicle multi-select dropdown ─────────────────────────────────────────────
// selectedVehicles: Set of vehicle ids. Empty set = "All".
function VehicleDropdown({ vehicles, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close when clicking outside
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const allSelected = selected.size === 0;

  const toggleAll = () => onChange(new Set()); // empty = show all

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    // If every vehicle is explicitly checked, normalise back to "all"
    if (next.size === vehicles.length) onChange(new Set());
    else onChange(next);
  };

  const buttonLabel = allSelected
    ? 'All Cars'
    : selected.size === 1
    ? vehicles.find(v => selected.has(v.id))?.name ?? '1 car selected'
    : `${selected.size} cars selected`;

  return (
    <div ref={ref} className={styles.vehDropWrap}>
      <button
        className={`${styles.vehDropBtn} ${!allSelected ? styles.vehDropBtnActive : ''}`}
        onClick={() => setOpen(o => !o)}
        title="Filter by car"
      >
        <span className={styles.vehDropLabel}>{buttonLabel}</span>
        <span className={styles.vehDropArrow}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className={styles.vehDropPanel}>
          {/* All Cars row */}
          <label className={`${styles.vehDropItem} ${allSelected ? styles.vehDropItemChecked : ''}`}>
            <span className={styles.vehCheckbox}>
              {allSelected ? '✓' : ''}
            </span>
            <span className={styles.vehDropName}>All Cars</span>
            <input type="checkbox" checked={allSelected} onChange={toggleAll} className={styles.hiddenCheck} />
          </label>

          {vehicles.length > 0 && <div className={styles.vehDropSep} />}

          {vehicles.map(v => {
            const checked = allSelected || selected.has(v.id);
            return (
              <label
                key={v.id}
                className={`${styles.vehDropItem} ${checked && !allSelected ? styles.vehDropItemChecked : ''}`}
              >
                <span className={styles.vehCheckbox}>
                  {checked ? '✓' : ''}
                </span>
                <span className={styles.vehDropName}>{v.name}</span>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(v.id)}
                  className={styles.hiddenCheck}
                />
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Corner speed sub-row ──────────────────────────────────────────────────────
function naturalSortCorners(corners) {
  return [...corners].sort((a, b) => {
    const label = (c) => (c.corner_label || c.corner_id || '');
    // Extract all numeric segments so "Turn 10" > "Turn 2"
    const parts = (s) => s.split(/(\d+)/).map((p, i) => i % 2 === 1 ? parseInt(p, 10) : p.toLowerCase());
    const pa = parts(label(a));
    const pb = parts(label(b));
    for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
      const va = pa[i] ?? '';
      const vb = pb[i] ?? '';
      if (va < vb) return -1;
      if (va > vb) return  1;
    }
    return 0;
  });
}

function CornerSpeeds({ corners, prefs, colSpan }) {
  const unit = speedLabel(prefs);
  if (!corners || corners.length === 0) {
    return (
      <tr className={styles.cornerRow}>
        <td colSpan={colSpan} className={styles.cornerCell}>
          <span className={styles.muted}>No corner speed data.</span>
        </td>
      </tr>
    );
  }
  return (
    <tr className={styles.cornerRow}>
      <td colSpan={colSpan} className={styles.cornerCell}>
        <table className={styles.cornerTable}>
          <thead>
            <tr>
              {naturalSortCorners(corners).map(c => (
                <th key={c.corner_id}>{c.corner_label || c.corner_id}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              {naturalSortCorners(corners).map(c => (
                <td key={c.corner_id} className={styles.tdMono}>
                  {fmt(msToSpeed(c.min_speed_ms, prefs), 1)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
        <span className={styles.cornerUnit}>Min speed through apex ({unit})</span>
      </td>
    </tr>
  );
}

// ── Admin: track-merge modal ──────────────────────────────────────────────────
// `tracks` are the canonical rows from the API; each carries a `members` list of
// the underlying track ids that fold into it. Admins merge a source track into a
// target canonical track, or un-merge a member back out.
function TrackMergeModal({ tracks, onMerge, onUnmerge, onRename, onClose }) {
  const [source, setSource]   = useState('');
  const [target, setTarget]   = useState('');
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState('');

  // Rename section state.
  const [renameId, setRenameId]     = useState('');
  const [renameName, setRenameName] = useState('');

  // Currently-merged groups: canonical rows with more than one member track.
  const mergedGroups = tracks.filter(t => (t.members?.length || 0) > 1);

  // Selecting a track to rename pre-fills the input with its current name.
  const pickRename = (id) => {
    setRenameId(id);
    setRenameName(tracks.find(t => t.track_id === id)?.track_name || '');
  };

  const doRename = async () => {
    const name = renameName.trim();
    if (!renameId || !name) return;
    setBusy(true); setError('');
    try {
      await onRename(renameId, name);
      setRenameId(''); setRenameName('');
    } catch (e) {
      setError(e.message || 'Rename failed');
    } finally {
      setBusy(false);
    }
  };

  const doMerge = async () => {
    if (!source || !target || source === target) return;
    setBusy(true); setError('');
    try {
      await onMerge(source, target);
      setSource(''); setTarget('');
    } catch (e) {
      setError(e.message || 'Merge failed');
    } finally {
      setBusy(false);
    }
  };

  const doUnmerge = async (trackId) => {
    setBusy(true); setError('');
    try {
      await onUnmerge(trackId);
    } catch (e) {
      setError(e.message || 'Un-merge failed');
    } finally {
      setBusy(false);
    }
  };

  const ov = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  };
  const panel = {
    background: '#1c1f26', color: '#e6e6e6', border: '1px solid #353a45',
    borderRadius: 10, padding: 20, width: 'min(560px, 92vw)',
    maxHeight: '86vh', overflowY: 'auto', boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
  };
  const sel = {
    background: '#13151a', color: '#e6e6e6', border: '1px solid #353a45',
    borderRadius: 6, padding: '6px 8px', minWidth: 160,
  };
  const btn = {
    background: '#2b6cb0', color: '#fff', border: 'none', borderRadius: 6,
    padding: '7px 14px', cursor: 'pointer', fontWeight: 600,
  };

  return (
    <div style={ov} onClick={onClose}>
      <div style={panel} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>Manage Tracks <span style={{ opacity: 0.6, fontSize: 13 }}>(admin)</span></h2>
          <button onClick={onClose} style={{ ...sel, minWidth: 0, cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ fontWeight: 600, marginBottom: 6 }}>Rename track</div>
        <p style={{ marginTop: 0, opacity: 0.75, fontSize: 13, lineHeight: 1.5 }}>
          Change a track's display name on the leaderboard. The new name is saved to the track
          definition too, so future uploads at this track keep it.
        </p>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: '14px 0' }}>
          <select style={sel} value={renameId} onChange={e => pickRename(e.target.value)} disabled={busy}>
            <option value="">— track —</option>
            {tracks.map(t => (
              <option key={t.track_id} value={t.track_id}>{t.track_name} ({t.track_id})</option>
            ))}
          </select>
          <input
            style={{ ...sel, minWidth: 200 }}
            type="text"
            placeholder="new name"
            value={renameName}
            onChange={e => setRenameName(e.target.value)}
            disabled={busy || !renameId}
          />
          <button
            style={{ ...btn, opacity: (!renameId || !renameName.trim() || busy) ? 0.5 : 1 }}
            onClick={doRename}
            disabled={!renameId || !renameName.trim() || busy}
          >Rename</button>
        </div>

        <div style={{ borderTop: '1px solid #353a45', paddingTop: 14, marginTop: 4 }} />

        <div style={{ fontWeight: 600, marginBottom: 6 }}>Merge tracks</div>
        <p style={{ marginTop: 0, opacity: 0.75, fontSize: 13, lineHeight: 1.5 }}>
          Fold a duplicate track (e.g. <em>DominionR</em>) into the track it should appear under
          (e.g. <em>Dominion</em>). Nothing in any session changes — only how the leaderboard groups
          laps. Future uploads of the duplicate fold in automatically. Reversible below.
        </p>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: '14px 0' }}>
          <span>Merge</span>
          <select style={sel} value={source} onChange={e => setSource(e.target.value)} disabled={busy}>
            <option value="">— source —</option>
            {tracks.map(t => (
              <option key={t.track_id} value={t.track_id}>{t.track_name} ({t.track_id})</option>
            ))}
          </select>
          <span>into</span>
          <select style={sel} value={target} onChange={e => setTarget(e.target.value)} disabled={busy}>
            <option value="">— target —</option>
            {tracks.filter(t => t.track_id !== source).map(t => (
              <option key={t.track_id} value={t.track_id}>{t.track_name} ({t.track_id})</option>
            ))}
          </select>
          <button
            style={{ ...btn, opacity: (!source || !target || source === target || busy) ? 0.5 : 1 }}
            onClick={doMerge}
            disabled={!source || !target || source === target || busy}
          >Merge</button>
        </div>

        {error && <div style={{ color: '#ff7a7a', fontSize: 13, marginBottom: 10 }}>⚠ {error}</div>}

        {mergedGroups.length > 0 && (
          <div style={{ borderTop: '1px solid #353a45', paddingTop: 12, marginTop: 6 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Current merges</div>
            {mergedGroups.map(g => (
              <div key={g.track_id} style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 13, marginBottom: 4 }}>
                  <strong>{g.track_name}</strong> <span style={{ opacity: 0.6 }}>({g.track_id})</span>
                </div>
                {g.members.filter(m => m.track_id !== g.track_id).map(m => (
                  <div key={m.track_id} style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 12, marginBottom: 4 }}>
                    <span style={{ fontSize: 13, opacity: 0.85 }}>↳ {m.track_name} ({m.track_id})</span>
                    <button
                      style={{ ...sel, minWidth: 0, cursor: 'pointer', fontSize: 12, padding: '3px 8px' }}
                      onClick={() => doUnmerge(m.track_id)}
                      disabled={busy}
                    >Un-merge</button>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function LeaderboardPage({
  onOpen3D = null, onBack, onLoadSession, onCompareSessions, unitPrefs, currentUser, initialParams, onParamsChange, onGoToProgress, onGoToChannelStats, onGoToSearch, onGoToSimLab, onOpenUserSettings, onLogout, driverProfile }) {
  const prefs = unitPrefs || { speed: 'mph', temp: 'F', pressure: 'psi' };
  const cols  = makeCols(prefs);

  const [tracks, setTracks]             = useState([]);
  const [tracksLoading, setTracksLoading] = useState(true);
  const [tracksError, setTracksError]   = useState('');

  // Unscoped track catalog for the reassign modal's target list — unlike `tracks`
  // (getLeaderboardTracks, scoped to tracks where THIS user has visible entries),
  // an admin must be able to move a session to any track definition, including
  // ones with no entries visible to them.
  const [allTracks, setAllTracks] = useState([]);
  useEffect(() => {
    if (!currentUser?.is_admin) return;
    getTracks().then(setAllTracks).catch(() => {});
  }, [currentUser?.is_admin]);

  const [selectedTrack,    setSelectedTrack]    = useState(null);
  const [selectedVehicles, setSelectedVehicles] = useState(new Set(initialParams?.vehicleIds || [])); // empty = all
  // Visibility scope: 'all' = my view (own + shared with me), 'public' = only
  // sessions shared with everyone (what the whole world sees).
  const [scope, setScope] = useState(initialParams?.scope || 'all');

  const [allEntries, setAllEntries] = useState([]);   // raw from API (all vehicles)
  const [loadingLb,  setLoadingLb]  = useState(false);
  const [lbError,    setLbError]    = useState('');

  const [sortCol, setSortCol] = useState(initialParams?.sortCol || 'lap_time_seconds');
  const [sortDir, setSortDir] = useState(initialParams?.sortDir || 'asc');

  const [expandedIds, setExpandedIds] = useState(new Set(initialParams?.expandedIds || []));        // corner-speed drill-down (by entry id)
  const [expandedDrivers, setExpandedDrivers] = useState(new Set(initialParams?.expandedDrivers || [])); // other-laps drill-down (by primary entry id)
  const [driverSort, setDriverSort] = useState(initialParams?.driverSort || {});                 // per-block lap sort: { [entryId]: { col, dir } }
  const setDriverSortFor = (id, next) => setDriverSort(prev => ({ ...prev, [id]: next }));
  const [loadingSessionId, setLoadingSessionId] = useState(null);

  const [mergeOpen, setMergeOpen] = useState(false);
  const [reassignEntry, setReassignEntry] = useState(null);   // admin: row being reassigned
  const isAdmin = !!currentUser?.is_admin;

  // Follow state: the set of user_ids the current user follows, so each driver
  // row can show a Follow/Following toggle. Loaded once when logged in.
  const myUserId = getCurrentUserId();
  const [followingIds, setFollowingIds] = useState(new Set());
  const [followBusy, setFollowBusy] = useState({});  // user_id -> true while toggling

  useEffect(() => {
    if (!currentUser) return;
    getFollowing()
      .then(list => setFollowingIds(new Set(list.map(u => u.id))))
      .catch(() => {});
  }, [currentUser]);

  const toggleFollow = useCallback(async (e, userId) => {
    e.stopPropagation();
    if (!userId) return;
    const isFollowing = followingIds.has(userId);
    setFollowBusy(prev => ({ ...prev, [userId]: true }));
    try {
      if (isFollowing) {
        await unfollowUser(userId);
        setFollowingIds(prev => { const n = new Set(prev); n.delete(userId); return n; });
      } else {
        await followUser(userId);
        setFollowingIds(prev => new Set(prev).add(userId));
      }
    } catch {
      // leave state unchanged on failure
    } finally {
      setFollowBusy(prev => ({ ...prev, [userId]: false }));
    }
  }, [followingIds]);

  // Compare selection: ordered list of picked entries (max MAX_COMPARE_SEL).
  // First = baseline (primary driver), the rest = comparison drivers.
  const [compareSel, setCompareSel] = useState([]);  // entry objects
  const [comparing,  setComparing]  = useState(false);
  // A restored compare selection only has entry ids (persisted state is
  // serialized, not full row objects) — re-hydrate into full entries once the
  // leaderboard has loaded, then forget it.
  const restoreCompareIdsRef = useRef(initialParams?.compareIds || null);
  useEffect(() => {
    if (!restoreCompareIdsRef.current?.length || !allEntries.length) return;
    const restored = restoreCompareIdsRef.current
      .map(id => allEntries.find(e => e.id === id))
      .filter(Boolean);
    if (restored.length) setCompareSel(restored);
    restoreCompareIdsRef.current = null;
  }, [allEntries]);

  const toggleCompare = useCallback((e, entry) => {
    e.stopPropagation();
    setCompareSel(prev => {
      const exists = prev.some(s => s.session_id === entry.session_id);
      if (exists) return prev.filter(s => s.session_id !== entry.session_id);
      if (prev.length >= MAX_COMPARE_SEL) return prev;  // at cap — ignore
      return [...prev, entry];
    });
  }, []);

  const clearCompare = useCallback(() => setCompareSel([]), []);

  const handleCompare = useCallback(async () => {
    if (compareSel.length < 2 || !onCompareSessions) return;
    setComparing(true);
    try {
      await onCompareSessions(
        compareSel[0].session_id,
        compareSel.slice(1).map(s => s.session_id),
      );
    } finally {
      setComparing(false);
    }
  }, [compareSel, onCompareSessions]);

  // Load track list, optionally keeping a specific track selected (by canonical id).
  // On first load, `initialParams.trackId` (from a deep-link/restore) is preferred;
  // after that the user's own selection (prev) wins, falling back to the track
  // with the most laps.
  const initialTrackIdRef = useRef(initialParams?.trackId);
  const loadTracks = useCallback((preserveId) => {
    return getLeaderboardTracks(scope)
      .then(data => {
        setTracks(data);
        setSelectedTrack(prev => {
          // priority: explicit preserveId (post-merge) > deep-link initial > prior selection > most laps
          const keepId = preserveId ?? initialTrackIdRef.current ?? prev?.track_id;
          initialTrackIdRef.current = null; // consume the deep-link on first use
          const mostLaps = data.reduce((best, t) =>
            (t.entry_count ?? 0) > (best?.entry_count ?? 0) ? t : best, data[0] ?? null);
          return data.find(t => t.track_id === keepId) || mostLaps || null;
        });
      })
      .catch(err => setTracksError(err.message))
      .finally(() => setTracksLoading(false));
  }, [scope]);

  useEffect(() => { loadTracks(); }, [loadTracks]);

  const handleMerge = useCallback(async (source, target) => {
    const res = await mergeTracks(source, target);
    await loadTracks(res.canonical_track_id);
  }, [loadTracks]);

  const handleUnmerge = useCallback(async (trackId) => {
    await unmergeTrack(trackId);
    await loadTracks();
  }, [loadTracks]);

  const handleRename = useCallback(async (trackId, name) => {
    await renameLeaderboardTrack(trackId, name);
    await loadTracks(trackId);
  }, [loadTracks]);

  // Fetch ALL entries for the track (no vehicle filter — we filter client-side).
  // Expanded-row / vehicle-filter resets on a genuine track change happen in the
  // effect that calls this, not here, so a restored Back-navigation isn't wiped.
  const loadLeaderboard = useCallback((track) => {
    if (!track) return;
    setLoadingLb(true);
    setLbError('');
    getLeaderboard(track.track_id, null, scope)
      .then(data => setAllEntries(data.entries || []))
      .catch(err => setLbError(err.message))
      .finally(() => setLoadingLb(false));
  }, [scope]);

  // First run (mount, possibly restoring a track from initialParams) keeps the
  // restored vehicle/expanded/compare selections; a genuine later track change
  // clears them, since they belong to the previous track's data.
  const firstTrackEffectRef = useRef(true);
  useEffect(() => {
    loadLeaderboard(selectedTrack);
    if (firstTrackEffectRef.current) {
      firstTrackEffectRef.current = false;
    } else {
      setSelectedVehicles(new Set());
      setExpandedIds(new Set());
      setExpandedDrivers(new Set());
      setCompareSel([]);
    }
  }, [selectedTrack, loadLeaderboard]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mirror the full filter/selection state up to App.jsx (→ URL) so a
  // Back-navigation or deep-link can restore this page exactly as left.
  useEffect(() => {
    onParamsChange?.({
      trackId: selectedTrack?.track_id || null,
      scope,
      vehicleIds: [...selectedVehicles],
      sortCol, sortDir,
      expandedIds: [...expandedIds],
      expandedDrivers: [...expandedDrivers],
      driverSort,
      compareIds: compareSel.map(s => s.id),
    });
  }, [selectedTrack, scope, selectedVehicles, sortCol, sortDir, expandedIds,
      expandedDrivers, driverSort, compareSel, onParamsChange]);

  // Admin: recompute a session onto the correct track, then refresh both the
  // track list (counts shift) and the current table (the session leaves if the
  // current track was the wrong one). Returns the API result for the modal.
  // Declared after loadLeaderboard so its dependency array can reference it.
  const handleReassign = useCallback(async (sessionId, trackId) => {
    const res = await reassignSessionTrack(sessionId, trackId);
    await loadTracks(selectedTrack?.track_id);
    loadLeaderboard(selectedTrack);
    return res;
  }, [loadTracks, loadLeaderboard, selectedTrack]);

  // Client-side vehicle filter → collapse to best lap per competitor → sort
  const filteredEntries = selectedVehicles.size === 0
    ? allEntries
    : allEntries.filter(e => selectedVehicles.has(e.vehicle_id));

  const collapsedEntries = collapseToBestLaps(filteredEntries);

  const sortedEntries = sortByCol(collapsedEntries, sortCol, sortDir, cols);

  const handleColClick = (col) => {
    if (sortCol === col.key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col.key);
      setSortDir(col.defaultDir);
    }
  };

  const handleTrackChange = (e) => {
    const t = tracks.find(t => t.track_id === e.target.value) || null;
    setSelectedTrack(t);
  };

  const handleRowClick = async (entry) => {
    if (!onLoadSession) return;
    setLoadingSessionId(entry.session_id);
    try {
      await onLoadSession(entry.session_id);
    } finally {
      setLoadingSessionId(null);
    }
  };

  const toggleExpand = (e, id) => {
    e.stopPropagation();
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleDriver = (e, id) => {
    e.stopPropagation();
    setExpandedDrivers(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const vehicles  = selectedTrack?.vehicles || [];
  const compareEnabled = !!onCompareSessions;
  const totalCols = cols.length + 1 + (compareEnabled ? 1 : 0) + (isAdmin ? 1 : 0); // +1 corners, +1 compare, +1 admin

  // ── Frozen left columns ───────────────────────────────────────────────────
  // The identity columns stay pinned while the metric columns scroll horizontally
  // on narrow displays. Each frozen column needs a fixed width so its `left`
  // offset (the cumulative width of everything frozen to its left) is exact; the
  // STICKY_W widths are also enforced on the cells (min/max/overflow) so the math
  // and the render agree. These columns are contiguous from the table's left edge:
  // [vs] # Driver Car Date "Lap Time".
  const FROZEN_KEYS = ['rank', 'user_display_name', 'vehicle_name', 'event_date', 'lap_time_seconds'];
  const STICKY_W = { vs: 44, rank: 54, user_display_name: 180, vehicle_name: 140, event_date: 112, lap_time_seconds: 104 };
  const stickyLeft = {};
  {
    let acc = 0;
    if (compareEnabled) { stickyLeft.vs = 0; acc += STICKY_W.vs; }
    for (const k of FROZEN_KEYS) { stickyLeft[k] = acc; acc += STICKY_W[k]; }
  }
  const stickyStyle = (key) => stickyLeft[key] == null ? undefined : {
    left: stickyLeft[key], width: STICKY_W[key], minWidth: STICKY_W[key], maxWidth: STICKY_W[key],
  };

  // Render a single entry as table rows (the main row + an optional corner-speed
  // row). `isChild` styles it as a drill-down lap under a competitor's best lap:
  // no rank/podium color, an indented driver cell, no +N badge.
  const renderRows = (entry, isChild) => {
    const isExpanded = expandedIds.has(entry.id);
    const isLoading  = loadingSessionId === entry.session_id;
    const rankClass  = isChild ? ''
                     : entry.rank === 1 ? styles.r1
                     : entry.rank === 2 ? styles.r2
                     : entry.rank === 3 ? styles.r3 : '';
    const compareIdx = compareSel.findIndex(s => s.session_id === entry.session_id);
    const driverOpen = expandedDrivers.has(entry.id);
    const isMine = !!myUserId && entry.user_id === myUserId;

    const rows = [
      <tr
        key={entry.id}
        className={`${styles.row} ${rankClass} ${isMine ? styles.mineRow : ''} ${isChild ? styles.childRow : ''} ${isLoading ? styles.rowLoading : ''}`}
        onClick={() => handleRowClick(entry)}
        title="Click to open this session"
      >
        {compareEnabled && (
          <td className={`${styles.td} ${styles.tdCorner} ${styles.stickyCol}`} style={stickyStyle('vs')} onClick={e => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={compareIdx !== -1}
              disabled={compareIdx === -1 && compareSel.length >= MAX_COMPARE_SEL}
              onChange={e => toggleCompare(e, entry)}
              title={compareIdx !== -1
                ? 'Remove from comparison'
                : compareSel.length >= MAX_COMPARE_SEL
                  ? `Comparison is full (max ${MAX_COMPARE_SEL})`
                  : `Add to comparison (pick 2–${MAX_COMPARE_SEL})`}
              aria-label="Select for comparison"
            />
          </td>
        )}
        {cols.map(col => {
          let content;
          if (col.key === 'rank') {
            content = isChild ? <span className={styles.muted}>↳</span> : col.render(entry);
          } else if (col.key === 'user_display_name' && isChild) {
            content = (
              <span className={styles.muted} title={entry.user_email}>
                ↳ {entry.user_display_name || entry.user_email || '—'}
              </span>
            );
          } else if (col.key === 'user_display_name') {
            content = (
              <span className={styles.driverCell}>
                <span title={entry.user_email}>{entry.user_display_name || entry.user_email || '—'}</span>
                {isMine && entry.share_status && (
                  <span
                    className={`${styles.sharePill} ${
                      entry.share_status === 'public' ? styles.sharePublic
                      : entry.share_status === 'shared' ? styles.shareShared
                      : styles.sharePrivate}`}
                    title={
                      entry.share_status === 'public'
                        ? 'Shared with everyone — other drivers can see this session on the public leaderboard.'
                        : entry.share_status === 'shared'
                        ? 'Shared with specific people or a group — only they see it. Not on the public leaderboard.'
                        : "Private — only you can see this lap. Share it to let others compare against you."}
                  >
                    {entry.share_status === 'public' ? 'Public'
                      : entry.share_status === 'shared' ? 'Shared'
                      : 'Private · only you'}
                  </span>
                )}
                {currentUser && entry.user_id && entry.user_id !== myUserId && (
                  <button
                    className={`${styles.followStar} ${followingIds.has(entry.user_id) ? styles.followStarActive : ''}`}
                    onClick={e => toggleFollow(e, entry.user_id)}
                    disabled={followBusy[entry.user_id]}
                    title={followingIds.has(entry.user_id)
                      ? `Unfollow ${entry.user_display_name || entry.user_email || 'this driver'}`
                      : `Follow ${entry.user_display_name || entry.user_email || 'this driver'} — get emailed when they share a session`}
                  >{followBusy[entry.user_id] ? '…' : followingIds.has(entry.user_id) ? '★' : '☆'}</button>
                )}
                {entry.otherLaps?.length > 0 && (
                  <button
                    className={`${styles.lapsBadge} ${driverOpen ? styles.lapsBadgeOpen : ''}`}
                    onClick={e => toggleDriver(e, entry.id)}
                    title={driverOpen
                      ? "Hide this driver's other laps"
                      : `Show ${entry.otherLaps.length} more lap${entry.otherLaps.length === 1 ? '' : 's'} from this driver`}
                  >{driverOpen ? '▾' : '▸'} +{entry.otherLaps.length}</button>
                )}
              </span>
            );
          } else {
            content = col.render(entry);
          }
          const sticky = stickyStyle(col.key);
          return (
            <td
              key={col.key}
              className={`${styles.td} ${col.key === 'lap_time_seconds' ? styles.tdTime : ''} ${sticky ? styles.stickyCol : ''} ${col.key === 'lap_time_seconds' ? styles.stickyColLast : ''}`}
              style={sticky}
            >
              {content}
            </td>
          );
        })}
        <td className={`${styles.td} ${styles.tdCorner}`} onClick={e => e.stopPropagation()}>
          {entry.corner_speeds?.length > 0 ? (
            <button
              className={`${styles.expandBtn} ${isExpanded ? styles.expandBtnOpen : ''}`}
              onClick={e => toggleExpand(e, entry.id)}
              title={isExpanded ? 'Hide corner speeds' : 'Show corner speeds'}
            >{isExpanded ? '▼' : '▶'}</button>
          ) : <span className={styles.muted}>—</span>}
        </td>
        {isAdmin && (
          <td className={`${styles.td} ${styles.tdCorner}`} onClick={e => e.stopPropagation()}>
            <button
              className={styles.expandBtn}
              onClick={e => { e.stopPropagation(); setReassignEntry(entry); }}
              title="Reassign this session to the correct track (admin)"
            >↪</button>
          </td>
        )}
      </tr>,
    ];
    if (isExpanded) {
      rows.push(
        <CornerSpeeds
          key={`${entry.id}_corners`}
          corners={entry.corner_speeds}
          prefs={prefs}
          colSpan={totalCols}
        />
      );
    }
    return rows;
  };

  return (
    <div className={styles.page}>

      {/* Top bar */}
      <div className={styles.topBar}>
        <div className={styles.topBarLeft}>
          <span className={styles.trophy}>🏆</span>
          <h1 className={styles.title}>Leaderboard</h1>
        </div>

        <div className={styles.topBarRight}>
          {/* Visibility scope — answers "what does the world see vs. what can I see?" */}
          <div className={styles.scopeToggle} role="group" aria-label="Leaderboard visibility">
            <button
              className={`${styles.scopeBtn} ${scope === 'all' ? styles.scopeBtnActive : ''}`}
              onClick={() => setScope('all')}
              title="Everything you can see: your own laps plus any session shared with you. Your private laps here are visible only to you."
            >My view</button>
            <button
              className={`${styles.scopeBtn} ${scope === 'public' ? styles.scopeBtnActive : ''}`}
              onClick={() => setScope('public')}
              title="Only sessions shared with everyone — what any driver on Track Analysis can see. You appear here only if you've shared with All users."
            >Public</button>
          </div>

          {tracksLoading ? (
            <span className={styles.muted}>Loading…</span>
          ) : tracksError ? (
            <span className={styles.errorText}>⚠ {tracksError}</span>
          ) : tracks.length === 0 ? (
            <span className={styles.muted}>No data yet</span>
          ) : (
            <select
              className={styles.trackSelect}
              value={selectedTrack?.track_id || ''}
              onChange={handleTrackChange}
            >
              {tracks.map(t => (
                <option key={t.track_id} value={t.track_id}>
                  {t.track_name} ({t.entry_count} {t.entry_count === 1 ? 'lap' : 'laps'})
                </option>
              ))}
            </select>
          )}

          {vehicles.length > 0 && (
            <VehicleDropdown
              vehicles={vehicles}
              selected={selectedVehicles}
              onChange={setSelectedVehicles}
            />
          )}

          {currentUser?.is_admin && tracks.length > 0 && (
            <button
              className={styles.backBtn}
              onClick={() => setMergeOpen(true)}
              title="Manage leaderboard tracks: rename or merge (admin)"
            >
              ⚙ Manage tracks
            </button>
          )}

        </div>
      </div>

      {mergeOpen && (
        <TrackMergeModal
          tracks={tracks}
          onMerge={handleMerge}
          onUnmerge={handleUnmerge}
          onRename={handleRename}
          onClose={() => setMergeOpen(false)}
        />
      )}

      {reassignEntry && (
        <ReassignTrackModal
          sessionId={reassignEntry.session_id}
          driver={reassignEntry.user_display_name || reassignEntry.user_email || 'Driver'}
          lapTimeStr={reassignEntry.lap_time_str}
          vehicleName={reassignEntry.vehicle_name}
          eventDate={reassignEntry.event_date}
          currentTrackName={selectedTrack?.track_name}
          tracks={allTracks}
          onReassign={handleReassign}
          onClose={() => setReassignEntry(null)}
        />
      )}

      {/* Hint bar */}
      <div className={styles.hintBar}>
        <span className={styles.muted}>
          {scope === 'public'
            ? 'Public board — only sessions shared with everyone. You appear here only if you shared with All users.'
            : 'My view — your own laps (visible only to you unless shared) plus sessions others shared with you. Badges show who can see each of your laps.'}
          {' · '}Best lap per driver · Click a row to open that session · Tick two ☑ to compare
        </span>
        {allEntries.length > 0 && (
          <span className={styles.muted}>
            {`${collapsedEntries.length} ${collapsedEntries.length === 1 ? 'driver' : 'drivers'} · ${filteredEntries.length} ${filteredEntries.length === 1 ? 'lap' : 'laps'}`}
            {filteredEntries.length !== allEntries.length ? ` (of ${allEntries.length})` : ''}
          </span>
        )}
      </div>

      {/* Table */}
      <div className={styles.tableWrap}>
        {loadingLb ? (
          <div className={styles.stateMsg}>Loading leaderboard…</div>
        ) : lbError ? (
          <div className={styles.errorText}>⚠ {lbError}</div>
        ) : sortedEntries.length === 0 ? (
          <div className={styles.stateMsg}>
            {tracksLoading ? '' : tracks.length === 0
              ? 'No leaderboard data yet. Upload and process sessions to populate the leaderboard.'
              : 'No entries for this selection.'}
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                {compareEnabled && (
                  <th className={`${styles.th} ${styles.thCorner} ${styles.stickyCol}`} style={stickyStyle('vs')} title="Pick two laps to compare">vs</th>
                )}
                {cols.map(col => {
                  const isActive = sortCol === col.key;
                  const sticky = stickyStyle(col.key);
                  return (
                    <th
                      key={col.key}
                      className={`${styles.th} ${isActive ? styles.thActive : ''} ${sticky ? styles.stickyCol : ''} ${col.key === 'lap_time_seconds' ? styles.stickyColLast : ''}`}
                      style={sticky}
                      onClick={() => handleColClick(col)}
                      title={col.tooltip ? `${col.tooltip}\n\nClick to sort` : `Sort by ${col.label}`}
                    >
                      <span className={styles.thLabel}>{col.label}</span>
                      {col.subLabel && <span className={styles.thSub}>{col.subLabel}</span>}
                      <span className={styles.sortIndicator}>
                        {isActive ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
                      </span>
                    </th>
                  );
                })}
                <th className={`${styles.th} ${styles.thCorner}`}>Corners</th>
                {isAdmin && (
                  <th className={`${styles.th} ${styles.thCorner}`} title="Admin: correct a mis-attributed session's track">Fix</th>
                )}
              </tr>
            </thead>
            <tbody>
              {sortedEntries.flatMap(entry => {
                const rows = renderRows(entry, false);
                if (expandedDrivers.has(entry.id) && entry.otherLaps?.length) {
                  const sort = driverSort[entry.id] || DEFAULT_LAP_SORT;
                  rows.push(
                    <DriverLapsSortRow
                      key={`${entry.id}_lapsort`}
                      entryId={entry.id}
                      cols={cols}
                      sort={sort}
                      onChange={setDriverSortFor}
                      colSpan={totalCols}
                      styles={styles}
                    />
                  );
                  const sortedLaps = sortByCol(entry.otherLaps, sort.col, sort.dir, cols);
                  for (const child of sortedLaps) rows.push(...renderRows(child, true));
                }
                return rows;
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <span className={styles.muted}>
          Speeds in {speedLabel(prefs)} · Distance in {distanceLabel(prefs)} · G values in g · metrics use inertial/ECU channels (incl. throttle/TPS for coasting) where available, GPS otherwise
        </span>
      </div>

      {/* Floating compare bar */}
      {compareEnabled && compareSel.length > 0 && (
        <div style={{
          position: 'fixed', left: '50%', bottom: 24, transform: 'translateX(-50%)',
          display: 'flex', alignItems: 'center', gap: 12, zIndex: 50,
          background: '#111827', border: '1px solid #334155', borderRadius: 10,
          padding: '10px 14px', boxShadow: '0 8px 24px rgba(0,0,0,0.45)', color: '#e5e7eb',
          maxWidth: '92vw', flexWrap: 'wrap',
        }}>
          <span style={{ fontWeight: 600 }}>Compare</span>
          {compareSel.map((s, i) => (
            <span key={s.session_id} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: '#1e293b', borderRadius: 16, padding: '4px 10px', fontSize: '0.85rem',
            }}>
              <span style={{ color: compareColor(i), fontWeight: 700 }}>
                {CIRCLED[i] || `#${i + 1}`}
              </span>
              {(s.user_display_name || s.user_email || 'Driver')} · {s.lap_time_str || s.vehicle_name}
              <button
                onClick={e => toggleCompare(e, s)}
                style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '1rem', lineHeight: 1 }}
                aria-label="Remove"
              >×</button>
            </span>
          ))}
          {compareSel.length < 2 && (
            <span style={{ opacity: 0.7, fontSize: '0.85rem' }}>Pick at least one more lap…</span>
          )}
          <button
            onClick={handleCompare}
            disabled={compareSel.length < 2 || comparing}
            style={{
              background: compareSel.length >= 2 ? '#2563eb' : '#374151',
              color: '#fff', border: 'none', borderRadius: 8, padding: '7px 14px',
              fontWeight: 600, cursor: compareSel.length >= 2 ? 'pointer' : 'not-allowed',
            }}
          >
            {comparing ? 'Loading…' : 'Compare these →'}
          </button>
          {onOpen3D && (
            /* The leaderboard is the one place laps from DIFFERENT drivers and
               sessions sit side by side, so this is where "race the track
               record" actually becomes possible. Laps are identified by
               lap_number here — the leaderboard's own identifier — and the
               backend maps it to a lap index. */
            <button
              onClick={() => onOpen3D(compareSel.slice(0, 4).map(sel => {
                // Re-read the row from allEntries by id rather than trusting the
                // object captured in compareSel. The captured copy can be stale
                // or partial — observed in production arriving with session_id,
                // user_display_name and vehicle_name but no lap_number or
                // lap_time_seconds, while the API demonstrably returns all four
                // — which left the request with no way to identify the lap.
                const e = allEntries.find(x => x.id === sel.id) || sel;
                return {
                  session_id: e.session_id,
                  // Both identifiers: lap_number is the leaderboard's own, and a
                  // row is fundamentally a lap TIME within a session, which the
                  // backend can also resolve.
                  lap_number: e.lap_number ?? undefined,
                  lap_time_seconds: e.lap_time_seconds ?? undefined,
                  name: e.user_display_name || e.user_email || 'Driver',
                  car: e.vehicle_name || undefined,
                };
              }))}
              disabled={compareSel.length < 1}
              style={{
                background: compareSel.length >= 1 ? '#0f766e' : '#374151',
                color: '#fff', border: 'none', borderRadius: 8, padding: '7px 14px',
                fontWeight: 600, cursor: compareSel.length >= 1 ? 'pointer' : 'not-allowed',
              }}
              title="Race the selected laps against each other in 3D"
            >
              View in 3D
            </button>
          )}
          <button
            onClick={clearCompare}
            style={{ background: 'none', border: '1px solid #374151', color: '#9ca3af', borderRadius: 8, padding: '7px 12px', cursor: 'pointer' }}
          >
            Clear
          </button>
        </div>
      )}
    </div>
  );
}
